<template>
  <b-row>
    <b-col cols="12">
      <b-row>
        <b-col cols="8">
          <b-card no-header>
            <template slot="header">
              Edit User
            </template>
            <b-row class="form-group">
              <b-col md="6">
                <label>First Name</label>
                <b-form-input type="text" placeholder="First Name" v-model.trim="user.contact.firstName"></b-form-input>
                <b-form-invalid-feedback>This is a required field</b-form-invalid-feedback>
              </b-col>
              <b-col md="6">
                <label>Last Name</label>
                <b-form-input type="text" placeholder="Last Name" v-model.trim="user.contact.lastName"></b-form-input>
                <b-form-invalid-feedback>This is a required field</b-form-invalid-feedback>
              </b-col>
            </b-row>
            <b-row class="form-group">
              <b-col md="4">
                <label>Username</label>
                <b-form-input type="text" placeholder="Username" v-model.trim="user.username" autocomplete="off"></b-form-input>
                <b-form-invalid-feedback>This is a required field</b-form-invalid-feedback>
              </b-col>
            </b-row>
            <div slot="footer">
              <b-btn size="sm" v-if="$can('update', 'users')" variant="primary" @click="save">Save</b-btn>
              <b-btn class="ml-1" v-if="$can('delete', 'users') && currentUser.id != user.id" size="sm" variant="danger" @click="del">Delete</b-btn>
            </div>
          </b-card>
        </b-col>
        <b-col cols="4">
          <b-card no-header>
            <template slot="header">
              Update Password
            </template>
            <b-row class="form-group">
              <b-col md="12">
                <b-form-group>
                  <b-input-group>
                    <b-form-input type="password" autocomplete="off" v-model="password" placeholder="Password" :state="passwordState"></b-form-input>
                    <b-input-group-append><b-input-group-text><i class="fa fa-asterisk"></i></b-input-group-text></b-input-group-append>
                    <b-form-invalid-feedback>Password needs to be 8 characters</b-form-invalid-feedback>
                  </b-input-group>
                </b-form-group>
              </b-col>
              <b-col md="12">
                <b-form-group>
                  <b-input-group>
                    <b-form-input type="password" autocomplete="off" v-model="verifyPassword" placeholder="Verify" :state="verifyPasswordState"></b-form-input>
                    <b-input-group-append><b-input-group-text><i class="fa fa-asterisk"></i></b-input-group-text></b-input-group-append>
                    <b-form-invalid-feedback>Passwords should match</b-form-invalid-feedback>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
            <template slot="footer">
              <b-row>
                <b-col sm="12" class="text-right">
                  <b-button :disabled="!canSavePassword" variant="primary" @click="savePassword">Save Password</b-button>
                </b-col>
              </b-row>
            </template>
          </b-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12" sm="4">
          <b-card v-if="$can('manage', 'roles')" class="mb-3" no-body>
            <template slot="header">Roles</template>
            <b-list-group flush>
              <b-list-group-item href="#" @click="toggleRole(user, role)" :variant="hasRole(user, role) ? 'success' : ''" v-for="role in userRoles" :key="`${user.id}-${role.id}`">
                <i class="fa" v-bind:class="{ 'fa-check-circle-o': hasRole(user, role), 'fa-circle-o': !hasRole(user, role) }"></i> {{ role.name }}
              </b-list-group-item>
            </b-list-group>
          </b-card>
        </b-col>
        <b-col cols="12" sm="4">
          <b-card v-if="$can('manage', 'userDevices')">
            <template slot="header">Devices</template>
            <b-row class="mb-2">
              <b-col cols="12">
                <b-list-group>
                  <b-list-group-item href="#" v-for="device in user.devices" :key="`${device.id}`">
                    <b-row>
                      <b-col cols="6" class="pt-1">
                        {{ device.name }}
                      </b-col>
                      <b-col cols="6" class="text-right pr-2">
                        <b-btn-group>
                          <b-btn size="sm" variant="danger" @click="deleteUserDevice(device.id)"><i class="fa fa-trash"></i></b-btn>
                        </b-btn-group>
                      </b-col>
                    </b-row>
                  </b-list-group-item>
                </b-list-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <combo-box v-model="addDeviceId" :options="deviceOptions" placeholder="Add a Device" @update="addUserDevice"></combo-box>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
        <b-col cols="12" sm="4">
          <b-card v-if="$can('manage', 'userMailboxes')">
            <template slot="header">Mailboxes</template>
            <b-row class="mb-2">
              <b-col cols="12">
                <b-list-group>
                  <b-list-group-item href="#" v-for="mailbox in user.mailboxes" :key="`${mailbox.id}`">
                    <b-row>
                      <b-col cols="6" class="pt-1">
                        {{ mailbox.mailbox }} - {{ mailbox.fullname }}
                      </b-col>
                      <b-col cols="6" class="text-right pr-2">
                        <b-btn-group>
                          <b-btn size="sm" variant="danger" @click="deleteUserMailbox(mailbox.id)"><i class="fa fa-trash"></i></b-btn>
                        </b-btn-group>
                      </b-col>
                    </b-row>
                  </b-list-group-item>
                </b-list-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12">
                <combo-box v-model="addMailboxId" :options="mailboxOptions" placeholder="Add a Mailbox" @update="addUserMailbox"></combo-box>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
import { mapGetters } from 'vuex'
import ComboBox from '@/components/form-elements/ComboBox'
// import AddMenuOptionModal from './partials/AddMenuOption.vue'

export default {
  name: 'User',
  components: {
    ComboBox
    // AddMenuOptionModal,
  },
  computed: {
    ...mapGetters({
      account: 'global/account',
      devices: 'account/devices',
      mailboxes: 'account/mailboxes',
      users: 'account/users',
      currentUser: 'global/user',
      roles: 'global/roles',
    }),
    deviceOptions () {
      if (!this.user.devices) return []
      const userDeviceIds = this.user.devices.map(o => o.id)
      return this.devices.filter(o => !userDeviceIds.includes(o.id)).map(o => ({ value: o.id, text: o.name }))
    },
    mailboxOptions () {
      if (!this.user.mailboxes) return []
      const userMailboxIds = this.user.devices.map(o => o.id)
      return this.mailboxes.filter(o => !userMailboxIds.includes(o.id)).map(o => ({ value: o.id, text: `${o.mailbox} - ${o.fullname}` }))
    },
    userRoles () {
      const roles = []
      const myRoles = []
      let minSort = null

      if (this.currentUser && this.currentUser.roles) {
        this.currentUser.roles.forEach((role) => {
          myRoles.push(role.id)
          if (!minSort || minSort > role.sort) {
            minSort = role.sort
          }
        })
      }

      if (this.currentUser && this.roles) {
        this.roles.forEach((role) => {
          if ((minSort !== null && (role.sort > minSort || minSort === 1)) || myRoles.includes(role.id)) {
            roles.push(role)
          }
        })
      }

      return roles.sort((a, b) => {
        if (a.sort > b.sort) return 1
        else if (a.sort < b.sort) return -1
        return 0
      })
    },
    passwordState () {
      if (this.password === null || this.password.length === 0) return null
      else if (this.password.length >= 8) return true
      return false
    },
    verifyPasswordState () {
      if (this.verifyPassword === null || this.verifyPassword.length === 0) return null
      else if (this.password && this.password == this.verifyPassword) return true
      return false
    },
    canSavePassword () {
      return (this.passwordState === true && this.verifyPasswordState === true)
    }
  },
  data () {
    return {
      addDeviceId: null,
      addMailboxId: null,
      caption: 'User',
      user: {
        contact: {
          firstName: null,
          lastName: null,
        },
        id: null,
        username: null,
        password: null,
        devices: [],
        mailboxes: [],
        roles: [],
      },
      optionNumber: null,
      password: null,
      verifyPassword: null,
    }
  },
  methods: {
    async save () {
      const id = this.$route.params.id
      const { data } = await this.$axios.put(`/v1/users/${id}`, this.user)
      Object.assign(this.user, data)
      await this.$store.dispatch('account/getUsers')
    },
    async savePassword () {
      const id = this.$route.params.id
      await this.$axios.put(`/v1/users/${id}`, { password: this.password })
      this.password = null
      this.verifyPassword = null
    },
    async addUserDevice (deviceId) {
      const userId = this.$route.params.id
      await this.$axios.post(`/v1/users/${userId}/devices`, { deviceId })
      await this.getUser(userId)
      this.$snotify.success('Device Added')
    },
    async deleteUserDevice (deviceId) {
      const userId = this.$route.params.id
      await this.$axios.delete(`/v1/users/${userId}/devices`, { data: { deviceId } })
      await this.getUser(userId)
      this.$snotify.success('Device Removed')
    },
    async addUserMailbox (mailboxId) {
      const userId = this.$route.params.id
      await this.$axios.post(`/v1/users/${userId}/mailboxes`, { mailboxId })
      await this.getUser(userId)
      this.$snotify.success('Mailbox Added')
    },
    async deleteUserMailbox (mailboxId) {
      const userId = this.$route.params.id
      await this.$axios.delete(`/v1/users/${userId}/mailboxes`, { data: { mailboxId } })
      await this.getUser(userId)
      this.$snotify.success('Mailbox Removed')
    },
    async del () {
      const id = this.$route.params.id
      await this.$axios.delete(`/v1/users/${id}`)
      await this.$store.dispatch('account/getUsers')
      this.$router.push({path: '/settings/users'})
    },
    async toggleRole (user, role) {
      if (this.hasRole(user, role)) {
        const { data } = await this.$axios.delete(`/v1/users/${user.id}/roles`, { data: { roleId: role.id } })
        this.user = data
      } else {
        const { data } = await this.$axios.post(`/v1/users/${user.id}/roles`, { roleId: role.id })
        this.user = data
      }
    },
    hasRole (user, role) {
      if (user) {
        return user.roles.map(o => o.id).includes(role.id)
      }
    },
    async getUser (id) {
      const user = this.users.find(o => o.id == id)
      if (user) this.user = user
      const { data } = await this.$axios.get(`/v1/users/${id}`)
      this.user = data
    },
  },
  created () {
    this.getUser(this.$route.params.id)
  },
}
</script>


